import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canUsePrebid } from '../utils/access';
import CprLogo from './CprLogo';

/* Sidebar (design 10, "flyout card"): only the section names show. Clicking a section opens a card
   beside the sidebar with its pages, each with a one-line description; the card's arrow points at the
   section that was clicked. Sections with one page go straight there.
   On a phone the menu slides in from the left and the section's pages replace it, with "‹ All sections".
   The sections and pages are exactly the ones CPR Live had before, per role.                           */

const P = {
  home: 'M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z', file: 'M6 3h9l4 4v14H6zM14 3v5h5', plus: 'M12 5v14M5 12h14',
  edit: 'M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4', doc: 'M6 3h9l4 4v14H6zM9 12h6M9 16h6', chart: 'M4 20V10M10 20V4M16 20v-7M2 20h20',
  cal: 'M3 6h18v15H3zM3 10h18M8 3v4M16 3v4', box: 'M4 7h16v13H4zM9 7V4h6v3', send: 'M3 11l18-8-8 18-2-8z', spark: 'M12 3l2.2 6.8L21 12l-6.8 2.2L12 21l-2.2-6.8L3 12l6.8-2.2z',
  mic: 'M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3zM5 11a7 7 0 0 0 14 0M12 18v3', cap: 'M2 8l10-4 10 4-10 4zM6 10v5c2 2 10 2 12 0v-5',
  chat: 'M21 12a8 8 0 0 1-12 7l-5 1 1-4a8 8 0 1 1 16-4z', img: 'M3 5h18v14H3zM3 16l5-5 4 4 3-3 6 6', star: 'M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z',
  calc: 'M5 3h14v18H5zM8 7h8M8 11h2M12 11h2M8 15h2M12 15h2M8 18h2M12 18h2', trophy: 'M8 4h8v5a4 4 0 0 1-8 0zM4 5h4v2a3 3 0 0 1-4 0zM16 5h4v2a3 3 0 0 1-4 0zM12 13v4M8 21h8M9 17h6',
  money: 'M12 3v18M17 7.5c0-1.9-2.2-3-5-3s-5 1.1-5 3 2.2 2.8 5 3.5 5 1.6 5 3.5-2.2 3-5 3-5-1.1-5-3', gear: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1',
  users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20c.8-3.5 3.4-5.5 6.5-5.5s5.7 2 6.5 5.5M16 4.8a3.5 3.5 0 0 1 0 6.4M18.5 14.8c1.5.9 2.6 2.6 3 5.2',
  shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z', card: 'M3 6h18v12H3zM7 10h4M7 14h6', brief: 'M4 7h16v13H4zM9 7V4h6v3M4 12h16',
  dash: 'M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z', chevr: 'M9 6l6 6-6 6', chevl: 'M15 6l-6 6 6 6', x: 'M6 6l12 12M18 6L6 18', menu: 'M4 7h16M4 12h16M4 17h16',
  out: 'M15 4h4v16h-4M10 16l4-4-4-4M14 12H3', user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c1-4 4-6 8-6s7 2 8 6',
};
const Ico = ({ n, size = 20, sw = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={P[n]} /></svg>
);

// [path, label, icon, one-line description]
const pg = (to, label, icon, desc) => ({ to, label, icon, desc });

function menuFor({ isAdmin, isStaff, isContractor, prebid }) {
  if (isContractor) return [{ key: 'work', label: 'My Work', icon: 'brief', pages: [pg('/contractor-portal', 'My Jobs', 'box', 'Jobs assigned to you')] }];
  const salesTools = [
    pg('/presentations', 'Presentations', 'doc', 'Decks to show the homeowner'),
    pg('/practice', 'Practice Room', 'mic', 'Rehearse the pitch with scripts'),
    pg('/training', 'CPR University', 'cap', 'Lessons, quizzes and tests'),
    pg('/objections', 'Objection Room', 'chat', 'Answers to what homeowners say'),
  ];
  const calcs = [
    pg('/painting-calculator', 'Commission Calculator', 'calc', 'Work out a commission'),
    ...(prebid ? [
      pg('/pre-bid', 'Pre-Bid Calculator', 'calc', 'Price a job before you bid'),
      pg('/pre-bid-manual', 'Pre-Bid (Manual)', 'calc', 'Enter your own costs'),
      pg('/pre-bid-roofing', 'Pre-Bid (Roofing)', 'calc', 'Roofing labor and material'),
    ] : []),
  ];
  if (isStaff) return [
    { key: 'work', label: 'My Work', icon: 'brief', pages: [
      pg('/my-dashboard', 'My Dashboard', 'dash', 'Your sales, goal and numbers'),
      pg('/my-jobs', 'My Jobs', 'box', 'Your jobs and commission'),
      pg('/send-contract', 'Send Document', 'send', 'Contracts and forms to sign'),
    ] },
    { key: 'tools', label: 'Sales Tools', icon: 'spark', pages: [
      ...salesTools, ...calcs,
      pg('/investment-review', 'Investment Review', 'chart', 'Walk through the investment'),
      pg('/projects', 'See Our Work', 'img', 'Photos of finished jobs'),
      pg('/testimonials', 'What Customers Say', 'star', 'Reviews to show homeowners'),
      pg('/board', 'Pace Board', 'chart', 'This month at a glance'),
    ] },
  ];
  return [
    { key: 'overview', label: 'Overview', icon: 'home', pages: [pg('/', 'CPR Live', 'home', 'The main dashboard')] },
    ...(isAdmin ? [{ key: 'records', label: 'Records', icon: 'file', sub: 'Everything about a job record', pages: [
      pg('/add', 'Add Record', 'plus', 'Enter a new sale'),
      pg('/update', 'Update Record', 'edit', 'Change an existing job'),
      pg('/cover-sheet', 'Cover Sheet', 'doc', 'Print for the crew'),
      pg('/on-site-report', 'On-Site Report', 'doc', 'Customer & scope summary'),
      pg('/pl-sheet', 'P&L Sheet', 'chart', 'Profit and loss for a job'),
    ] }] : []),
    ...(isAdmin ? [{ key: 'appts', label: 'Appointments', icon: 'cal', sub: 'From booking to result', pages: [
      pg('/book', 'Book Appointment', 'plus', 'Enter a new appointment'),
      pg('/dispatch', 'Dispatch Board', 'cal', 'Give the day\'s appointments to reps'),
      pg('/appointments', 'Performance', 'chart', 'Reps, setters and confirmers'),
    ] }] : []),
    { key: 'tracker', label: isAdmin ? 'Tracker' : 'Records', icon: 'box', sub: 'Follow every job', pages: [
      pg('/tracker', 'Job Tracker', 'box', 'Every job and where it stands'),
      pg('/send-contract', 'Send Document', 'send', 'Contracts and forms to sign'),
      pg('/calendar', 'Calendar', 'cal', 'Installs and inspections'),
    ] },
    { key: 'tools', label: 'Sales Tools', icon: 'spark', sub: 'For the sales floor', pages: [
      ...salesTools,
      ...(isAdmin ? [pg('/training-admin', 'University Admin', 'doc', 'Edit lessons, unlock tests')] : []),
      pg('/investment-review', 'Investment Review', 'chart', 'Walk through the investment'),
      pg('/projects', 'See Our Work', 'img', 'Photos of finished jobs'),
      pg('/testimonials', 'What Customers Say', 'star', 'Reviews to show homeowners'),
    ] },
    { key: 'calcs', label: 'Calculators', icon: 'calc', sub: 'Price and commission', pages: calcs },
    { key: 'reports', label: 'Reports', icon: 'chart', sub: 'Sales and money, from the records', pages: [
      pg('/board', 'Pace Board', 'chart', 'This month at a glance'),
      pg('/staff-report', 'Leaderboard', 'trophy', 'Who sold the most'),
      pg('/sales', 'Sales Report', 'chart', 'Every job sold in a period'),
      pg('/collection', 'Collection', 'money', 'What to collect and when'),
      ...(isAdmin ? [pg('/cashflow', 'Cashflow', 'money', 'Every job\'s money, by product')] : []),
    ] },
    ...(isAdmin ? [{ key: 'manage', label: 'Management', icon: 'gear', sub: 'People, settings and safety', pages: [
      pg('/contractors', 'Contractors', 'users', 'Crews and companies'),
      pg('/staff', 'Staff Profiles', 'users', 'Everyone at CPR'),
      pg('/forms', 'Forms List', 'doc', 'Dropdown choices'),
      pg('/users', 'Users', 'users', 'Logins and roles'),
      pg('/quickbooks', 'QuickBooks', 'money', 'Sync with QuickBooks'),
      pg('/data-safety', 'Data Safety', 'shield', 'Recycle bin and activity'),
      pg('/id-cards', 'ID Cards', 'card', 'Print staff ID cards'),
      pg('/change-orders', 'Change Orders', 'edit', 'Changes to a job'),
      pg('/contractor-packets', 'Contractor Packets', 'box', 'Paperwork for crews'),
      pg('/canvasser-payroll', 'Canvasser Payroll', 'money', 'Pay canvassers and setters'),
    ] }] : []),
  ];
}

const CSS = `
:root{--sidebar-width:236px}
.sidebar.sb10{background:#0D3B2E;box-shadow:none;font-family:'Inter',system-ui,sans-serif;overflow:visible}
.sb10-logo{display:flex;align-items:center;gap:10px;padding:18px 18px 14px}
.sb10-logo b{font-family:'Fraunces',Georgia,serif;font-size:21px;color:#fff;font-weight:700;line-height:1}
.sb10-nav{flex:1;overflow-y:auto;padding:4px 12px}
.sb10-sec{display:flex;align-items:center;gap:14px;width:100%;min-height:48px;padding:0 14px;margin:2px 0;border:0;border-radius:12px;background:none;color:#D7E3DC;font:600 15.5px 'Inter',system-ui,sans-serif;text-align:left;cursor:pointer;text-decoration:none;position:relative}
.sb10-sec svg{color:#8FB3A1;flex-shrink:0}
.sb10-sec:hover{background:rgba(255,255,255,.07);color:#fff}
.sb10-sec .chev{margin-left:auto;display:flex}
.sb10-sec.here::before{content:"";position:absolute;left:4px;top:14px;bottom:14px;width:3px;border-radius:2px;background:#B8912E}
.sb10-sec.here{color:#fff}
.sb10-sec.open,.sb10-sec.open:hover,.sb10-sec.active-one{background:#F3EAD3;color:#0D3B2E;font-weight:800}
.sb10-sec.open svg,.sb10-sec.active-one svg{color:#0D3B2E}
.sb10-sec.open::before{display:none}
.sb10-sec:focus-visible,.sb10-item:focus-visible,.sb10-btn:focus-visible{outline:3px solid #F0C842;outline-offset:2px}
.sb10-foot{border-top:1px solid #1F5643;padding:12px 16px 14px;display:flex;align-items:center;gap:10px}
.sb10-av{width:36px;height:36px;border-radius:50%;background:#B8912E;color:#0D3B2E;display:flex;align-items:center;justify-content:center;font:800 13px 'Inter',sans-serif;flex-shrink:0;text-transform:uppercase}
.sb10-who{flex:1;min-width:0;color:#fff;font-size:13px;font-weight:600;line-height:1.3}
.sb10-who small{display:block;color:#8FB3A1;font-weight:500;text-transform:capitalize}
.sb10-who button{border:0;background:none;padding:0;color:#F3EAD3;font:600 12px 'Inter',sans-serif;cursor:pointer;text-decoration:underline}
.sb10-btn{border:0;background:rgba(255,255,255,.08);color:#C9D8D0;width:38px;height:38px;border-radius:10px;display:flex;align-items:center;justify-content:center;cursor:pointer}
.sb10-btn:hover{background:rgba(255,255,255,.16);color:#fff}
/* the card */
.sb10-card{position:fixed;left:calc(var(--sidebar-width) + 10px);width:310px;max-height:calc(100vh - 24px);overflow-y:auto;background:#fff;border-radius:16px;
  box-shadow:0 24px 60px -18px rgba(9,30,23,.45),0 2px 6px rgba(0,0,0,.08);padding:16px 12px 12px;z-index:160;animation:sb10in .14s ease-out}
.sb10-arrow{position:fixed;left:calc(var(--sidebar-width) + 4px);width:14px;height:14px;background:#fff;transform:rotate(45deg);z-index:161;box-shadow:-2px 2px 3px rgba(0,0,0,.04)}
.sb10-card h3{margin:0 6px 2px;font:700 19px 'Fraunces',Georgia,serif;color:#13211B}
.sb10-card p{margin:0 6px 10px;font-size:12.5px;color:#5E6E66}
.sb10-item{display:flex;gap:12px;align-items:center;padding:9px 10px;border-radius:11px;text-decoration:none;color:#13211B}
.sb10-item:hover{background:#F4F7F5}
.sb10-item.active{background:#EAF3EE}
.sb10-item .ic{width:38px;height:38px;border-radius:10px;background:#EEF2EF;color:#0D3B2E;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.sb10-item.active .ic{background:#0D3B2E;color:#F0C842}
.sb10-item b{display:block;font-size:14.5px;font-weight:700}
.sb10-item small{display:block;font-size:12.5px;color:#5E6E66}
.sb10-dim{position:fixed;inset:0;background:rgba(13,33,27,.16);z-index:140}
@keyframes sb10in{from{opacity:0;transform:translateX(-6px)}}
/* phone */
.hamburger-btn.sb10-ham{background:#0D3B2E;color:#F3EAD3;border-radius:12px;width:44px;height:44px;padding:0;top:8px;left:10px}
.sb10-back{display:inline-flex;align-items:center;gap:6px;min-height:44px;padding:0 12px 0 8px;margin:4px 0 6px;border:0;border-radius:10px;background:#EEF2EF;color:#0D3B2E;font:700 15px 'Inter',sans-serif;cursor:pointer}
@media(max-width:768px){
  .sidebar.sb10{width:min(318px,82vw)}
  .hamburger-btn.sb10-ham.is-open{left:calc(min(318px,82vw) + 10px);z-index:1200}
  .sb10-sec{min-height:52px;font-size:16.5px}
  .sb10-sub{position:absolute;inset:0;background:#fff;z-index:2;display:flex;flex-direction:column;padding:16px 12px;overflow-y:auto;animation:sb10in .16s ease-out}
  .sb10-sub h3{margin:10px 10px 2px;font:700 26px 'Fraunces',Georgia,serif;color:#13211B}
  .sb10-sub p{margin:0 10px 12px;font-size:13px;color:#5E6E66}
  .sb10-sub .sb10-item{min-height:60px}
  .sb10-sub .sb10-item b{font-size:16px}
}
@media(prefers-reduced-motion:reduce){.sb10-card,.sb10-sub{animation:none}}
@media print{.sb10-card,.sb10-arrow,.sb10-dim{display:none!important}}
`;

export default function Sidebar() {
  const auth = useAuth();
  const { username, signOut, isAdmin, isStaff, isContractor, role, staff_name } = auth;
  const prebid = canUsePrebid(auth);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openKey, setOpenKey] = useState(null);       // section whose card is showing
  const [pos, setPos] = useState({ top: 0, arrow: 0 });
  const btnRefs = useRef({});
  const cardRef = useRef(null);
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);

  const menu = menuFor({ isAdmin, isStaff, isContractor, prebid });
  const isHere = p => (p.to === '/' ? pathname === '/' : pathname === p.to || pathname.startsWith(p.to + '/'));
  const sectionHere = s => s.pages.some(isHere);
  const open = menu.find(s => s.key === openKey);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const on = () => setPhone(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, []);
  // a page was picked: close everything
  useEffect(() => { setOpenKey(null); setMobileOpen(false); }, [pathname]);
  // Esc closes the card
  useEffect(() => {
    if (!openKey) return undefined;
    const k = e => { if (e.key === 'Escape') { const b = btnRefs.current[openKey]; setOpenKey(null); b && b.focus(); } };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [openKey]);
  // place the card beside the section that was clicked, arrow pointing at it
  useLayoutEffect(() => {
    if (!openKey || phone) return;
    const place = () => {
      const b = btnRefs.current[openKey], c = cardRef.current;
      if (!b || !c) return;
      const r = b.getBoundingClientRect(), mid = r.top + r.height / 2;
      const h = c.offsetHeight, vh = window.innerHeight;
      const top = Math.max(12, Math.min(mid - 34, vh - h - 12));
      setPos({ top, arrow: mid - 7 });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [openKey, phone]);

  function clickSection(s) {
    if (s.pages.length === 1) { navigate(s.pages[0].to); setOpenKey(null); setMobileOpen(false); return; }
    setOpenKey(k => (k === s.key ? null : s.key));
  }
  function handleLogout() { signOut(); navigate('/login'); }
  const display = staff_name || username || '';
  const initials = display.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('') || '?';

  const items = s => s.pages.map(p => (
    <NavLink key={p.to + p.label} to={p.to} end={p.to === '/'} className={({ isActive }) => 'sb10-item' + (isActive ? ' active' : '')} onClick={() => { setOpenKey(null); setMobileOpen(false); }}>
      <span className="ic"><Ico n={p.icon} size={19} /></span><span><b>{p.label}</b><small>{p.desc}</small></span>
    </NavLink>
  ));

  return (
    <>
      <style>{CSS}</style>
      <button type="button" className={'hamburger-btn sb10-ham' + (mobileOpen ? ' is-open' : '')} onClick={() => { setMobileOpen(o => !o); setOpenKey(null); }} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen}>
        <Ico n={mobileOpen ? 'x' : 'menu'} size={22} sw={2.2} />
      </button>
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      <aside className={`sidebar sb10${mobileOpen ? ' open' : ''}`} aria-label="Main menu">
        <div className="sb10-logo"><CprLogo height={34} /><b>CPR Live</b></div>

        <nav className="sb10-nav">
          {menu.filter(s => s.pages.length).map(s => {
            const one = s.pages.length === 1;
            const here = sectionHere(s);
            return (
              <button key={s.key} type="button" ref={el => { btnRefs.current[s.key] = el; }}
                className={'sb10-sec' + (openKey === s.key ? ' open' : '') + (one && here ? ' active-one' : here ? ' here' : '')}
                aria-expanded={one ? undefined : openKey === s.key} aria-current={one && here ? 'page' : undefined}
                onClick={() => clickSection(s)}>
                <Ico n={s.icon} size={20} />{s.label}
                {!one && <span className="chev"><Ico n="chevr" size={16} sw={2} /></span>}
              </button>
            );
          })}
        </nav>

        <div className="sb10-foot">
          <span className="sb10-av" aria-hidden="true">{initials}</span>
          <div className="sb10-who">{display}<small>{role}</small>
            {!isContractor && <button type="button" onClick={() => navigate('/my-profile')}>My profile</button>}
          </div>
          <button type="button" className="sb10-btn" onClick={handleLogout} aria-label="Sign out" title="Sign out"><Ico n="out" size={18} /></button>
        </div>

        {/* phone: the section's pages replace the menu */}
        {phone && open && (
          <div className="sb10-sub" role="region" aria-label={open.label}>
            <button type="button" className="sb10-back" onClick={() => setOpenKey(null)}><Ico n="chevl" size={18} sw={2.4} />All sections</button>
            <h3>{open.label}</h3>{open.sub && <p>{open.sub}</p>}
            {items(open)}
          </div>
        )}
      </aside>

      {/* desktop: the card beside the sidebar */}
      {!phone && open && (
        <>
          <div className="sb10-dim" onMouseDown={() => setOpenKey(null)} />
          <span className="sb10-arrow" style={{ top: pos.arrow }} aria-hidden="true" />
          <div className="sb10-card" ref={cardRef} style={{ top: pos.top }} role="region" aria-label={open.label}>
            <h3>{open.label}</h3>{open.sub && <p>{open.sub}</p>}
            {items(open)}
          </div>
        </>
      )}
    </>
  );
}
